"use client";

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useFreeOfferEnabled } from "@/contexts/CommerceSettingsContext";

function formatDuration(seconds, t) {
    if (!seconds || seconds <= 0) return null;
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (minutes < 60) return t("ShowPart_minutes", { minutes });
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0
        ? t("ShowPart_hoursMinutes", { hours: h, minutes: m })
        : t("ShowPart_hours", { hours: h });
}

function getDifficultyLabel(difficulty, t) {
    switch (difficulty) {
        case "beginner":
            return t("ShowPart_difficulty_beginner");
        case "intermediate":
            return t("ShowPart_difficulty_intermediate");
        case "advanced":
            return t("ShowPart_difficulty_advanced");
        default:
            return null;
    }
}

function getChallengeDurationLabel(config, t) {
    const min = config?.estimatedDurationMin;
    const max = config?.estimatedDurationMax;
    if (!min && !max) return null;
    if (min && max && min !== max) {
        return t("ShowPart_durationRange", { min, max });
    }
    return t("ShowPart_minutes", { minutes: max || min });
}

const TYPE_STYLES = {
    course: {
        hoverBorder: "hover:border-primary/40 dark:hover:border-primary/40",
        iconWrap:
            "bg-primary/10 dark:bg-primary/20 ring-1 ring-primary/10 dark:ring-primary/20",
        iconColor: "text-primary dark:text-primary-100",
        iconName: "book-bookmark-bold-duotone",
    },
    quiz: {
        hoverBorder: "hover:border-sky-400/50 dark:hover:border-sky-500/40",
        iconWrap:
            "bg-sky-50 dark:bg-sky-500/20 ring-1 ring-sky-200/60 dark:ring-sky-500/20",
        iconColor: "text-sky-600 dark:text-sky-400",
        iconName: "question-square-bold-duotone",
    },
    challenge: {
        hoverBorder:
            "hover:border-orange-400/60 dark:hover:border-orange-500/40",
        iconWrap:
            "bg-gradient-to-br from-orange-100 to-amber-50 dark:from-orange-500/25 dark:to-amber-500/10 ring-1 ring-orange-200/60 dark:ring-orange-500/20",
        iconColor: "text-orange-600 dark:text-orange-400",
        iconName: "code-square-bold-duotone",
    },
};

function MetaLine({ items, accent }) {
    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[12px] text-gray-500 dark:text-grayDark-400">
            {items.map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                    {i > 0 && (
                        <span
                            className="text-gray-300 dark:text-grayDark-600"
                            aria-hidden
                        >
                            ·
                        </span>
                    )}
                    {item.highlight ? (
                        <span
                            className={`font-semibold ${accent || "text-emerald-600 dark:text-emerald-400"}`}
                        >
                            {item.label}
                        </span>
                    ) : (
                        <span>{item.label || item}</span>
                    )}
                </span>
            ))}
        </div>
    );
}

export default function ShowPart({ part, partdur, expandTrigger, collapseTrigger, ...props }) {
    const t = useTranslations();
    // Offre gratuite coupée depuis /admin/promotions : plus de badge
    // « Gratuit » sur les séances, la porte étant fermée côté serveur.
    const freeOfferEnabled = useFreeOfferEnabled();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (props.first) {
            setIsOpen(true);
        }
    }, [props.first]);

    useEffect(() => {
        if (expandTrigger && expandTrigger > 0) setIsOpen(true);
    }, [expandTrigger]);

    useEffect(() => {
        if (collapseTrigger && collapseTrigger > 0) setIsOpen(false);
    }, [collapseTrigger]);

    const hasFreeAccess =
        freeOfferEnabled &&
        part.sessions?.some((s) => s.isFreeToAccess && s.online);
    const onlineSessionsCount =
        part.sessions?.filter((s) => s.online).length || 0;

    const type = part.isChallenge
        ? "challenge"
        : part.isQuiz
            ? "quiz"
            : "course";
    const style = TYPE_STYLES[type];

    if (type === "challenge") {
        const config = part.challengeConfig || {};
        const difficulty = getDifficultyLabel(config.difficulty, t);
        const durationLabel = getChallengeDurationLabel(config, t);

        const metaItems = [];
        if (hasFreeAccess)
            metaItems.push({
                label: t("ShowPart_freeAccess"),
                highlight: true,
            });
        metaItems.push(t("ShowPart_interactiveChallenge"));
        if (difficulty) metaItems.push(difficulty);
        if (durationLabel) metaItems.push(durationLabel);

        return (
            <div
                className={`flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-gray-200 dark:border-newDark-700 bg-white dark:bg-newDark-800/30 transition-all ${style.hoverBorder} hover:shadow-sm`}
            >
                <div
                    className={`shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center ${style.iconWrap}`}
                >
                    <SolarIcon
                        name={style.iconName}
                        className={`w-5 h-5 md:w-6 md:h-6 ${style.iconColor}`}
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] md:text-base font-semibold text-black dark:text-white tracking-tight leading-snug">
                        {part.title}
                    </h3>
                    <p className="mt-1 text-[13px] md:text-sm text-gray-600 dark:text-lightBlue leading-snug mb-0">
                        {part.summary || t("ShowPart_challenge_subtitle")}
                    </p>
                    <MetaLine
                        items={metaItems}
                        accent="text-orange-600 dark:text-orange-400"
                    />
                </div>
            </div>
        );
    }

    if (type === "quiz") {
        const questionsCount = part.sessions?.length || 0;
        const metaItems = [t("ShowPart_interactiveQuiz")];
        if (questionsCount > 0) {
            metaItems.push(
                questionsCount > 1
                    ? t("ShowPart_questionsCount", { count: questionsCount })
                    : t("ShowPart_questionCount"),
            );
        }

        return (
            <div
                className={`flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-gray-200 dark:border-newDark-700 bg-white dark:bg-newDark-800/30 transition-all ${style.hoverBorder} hover:shadow-sm`}
            >
                <div
                    className={`shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center ${style.iconWrap}`}
                >
                    <SolarIcon
                        name={style.iconName}
                        className={`w-5 h-5 md:w-6 md:h-6 ${style.iconColor}`}
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] md:text-base font-semibold text-black dark:text-white tracking-tight leading-snug">
                        {part.title}
                    </h3>
                    <MetaLine
                        items={metaItems}
                        accent="text-sky-600 dark:text-sky-400"
                    />
                </div>
            </div>
        );
    }

    const arrowVariants = {
        open: { rotate: -180 },
        closed: { rotate: 0 },
    };
    const contentVariants = {
        open: { opacity: 1, height: "auto", overflow: "visible" },
        closed: { opacity: 0, height: 0, overflow: "hidden" },
    };

    const metaItems = [];
    if (hasFreeAccess)
        metaItems.push({ label: t("ShowPart_freeAccess"), highlight: true });
    if (partdur > 0) metaItems.push(formatDuration(partdur, t));
    if (onlineSessionsCount > 0) {
        metaItems.push(
            onlineSessionsCount > 1
                ? t("ShowPart_sessionsCount", { count: onlineSessionsCount })
                : t("ShowPart_sessionCount"),
        );
    }

    return (
        <div
            className={`rounded-2xl border border-gray-200 dark:border-newDark-700 bg-white dark:bg-newDark-800/30 transition-all ${style.hoverBorder} hover:shadow-sm`}
        >
            <div
                className="flex items-start gap-4 p-4 md:p-5 cursor-pointer"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div
                    className={`shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center ${style.iconWrap}`}
                >
                    <SolarIcon
                        name={style.iconName}
                        className={`w-5 h-5 md:w-6 md:h-6 ${style.iconColor}`}
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="text-[15px] md:text-base font-semibold text-black dark:text-white tracking-tight leading-snug">
                            {part.title}
                        </h3>
                        <motion.div
                            initial={false}
                            animate={isOpen ? "open" : "closed"}
                            variants={arrowVariants}
                            transition={{ duration: 0.2 }}
                            className="shrink-0 mt-0.5 text-gray-400 dark:text-grayDark-500"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M18 9s-4.419 6-6 6s-6-6-6-6"
                                    color="currentColor"
                                ></path>
                            </svg>
                        </motion.div>
                    </div>
                    {part.summary && (
                        <p className="mt-1 text-[13px] md:text-sm text-gray-600 dark:text-lightBlue leading-snug mb-0">
                            {part.summary}
                        </p>
                    )}
                    <MetaLine items={metaItems} />
                </div>
            </div>

            <motion.div
                initial={false}
                animate={isOpen ? "open" : "closed"}
                variants={contentVariants}
                transition={{ duration: 0.3, ease: "easeInOut" }}
            >
                <div className="px-4 pb-4 md:px-5 md:pb-5">
                    <div className="border-t border-gray-100 dark:border-newDark-700/60 pt-3 flex flex-col md:pl-[64px]">
                        {part.sessions &&
                            part.sessions
                                .sort((a, b) => a.position - b.position)
                                .map(
                                    (session, index) =>
                                        session.online && (
                                            <div
                                                key={`session-${index}`}
                                                id={`session-${session.id}`}
                                                className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-newDark-700/40 transition-colors group"
                                            >
                                                <SolarIcon
                                                    name={
                                                        session.video
                                                            ? "play-circle-bold-duotone"
                                                            : "document-text-bold-duotone"
                                                    }
                                                    className="w-4 h-4 text-gray-400 dark:text-grayDark-500 shrink-0 group-hover:text-primary dark:group-hover:text-primary-100 transition-colors"
                                                />
                                                <span className="text-[14px] text-black dark:text-white flex-1">
                                                    {session.title}
                                                </span>
                                                {freeOfferEnabled && session.isFreeToAccess && (
                                                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">
                                                        {t(
                                                            "ShowPart_freeAccess_session",
                                                        )}
                                                    </span>
                                                )}
                                                {session.duration > 0 && (
                                                    <span className="text-xs text-gray-500 dark:text-grayDark-400 shrink-0 tabular-nums">
                                                        {formatDuration(
                                                            session.duration,
                                                            t,
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        ),
                                )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
